import {
  Box,
  // ECRH-5475 — Denied checkbox commented out in the claim breakdown; the
  // Status column already reads "Denied". Re-enable with the checkbox column.
  // Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Text from "../../../../../components/Text";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import {
  DEFAULT_FONTSIZE,
  DEFAULT_TEXT_FONT_WEIGHT,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import { ERA_PROCEDURE_COLUMNS } from "./constants";
import { PatientProcedureCode } from "../../../../../services";
import {
  CPT_POSTING_STATUS_DISPLAY,
  CptAmounts,
  deriveCptAmounts,
} from "./era-cpt";

const fmt = (n: number | null) => (n === null ? "-" : `$ ${n.toFixed(2)}`);

// Sums that ignore missing values, but stay `null` when no row had a value at
// all — so an untouched column totals to "-" rather than $ 0.00.
const add = (acc: number | null, v: number | null): number | null =>
  v === null ? acc : (acc ?? 0) + v;

const cellSx = { py: 1, px: 1 };

// ECRH-5475 — a denied line's payment figures are greyed / de-emphasised, the
// same way the ERA posting screen shows them.
const deniedAmountSx = { color: "text.disabled", opacity: 0.6 };

const headerSx = {
  bgcolor: "grey.100",
  "& th": {
    fontSize: DEFAULT_FONTSIZE,
    fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
    py: 1.2,
    px: 1,
    borderBottom: "1px solid",
    borderColor: "grey.300",
    whiteSpace: "nowrap",
  },
};

const extractCpt = (pc: PatientProcedureCode) => {
  const obj = pc.procedureCode ?? {};
  const entry = (Object.values(obj)?.[0] as string) ?? "";
  const [cpt = ""] = entry.split(" - ");
  return cpt.trim() || (Object.keys(obj)?.[0] ?? "-");
};

// The Status column reflects the line's posting status (Unpaid / Partially Paid
// / Paid / Denied). Absent on a never-adjudicated line -> nothing to show yet.
const StatusCell = ({ pc }: { pc: PatientProcedureCode }) => {
  const raw = pc.postingStatus;
  if (!raw) {
    return (
      <Text variant="body2" color="text.secondary">
        -
      </Text>
    );
  }
  const display = CPT_POSTING_STATUS_DISPLAY[raw];
  return (
    <Text variant="body2" sx={{ color: display?.color ?? "text.primary" }}>
      {display?.label ?? replaceUnderScoreAddSpace(raw)}
    </Text>
  );
};

interface EraProcedureSubTableProps {
  procedures: PatientProcedureCode[];
  // ECRH-4700 — when provided, renders a per-CPT "Post" button.
  onPostCpt?: (pcIndex: number) => void;
  // Disables every CPT post button (e.g. missing permission).
  postDisabled?: boolean;
}

const EraProcedureSubTable = ({
  procedures,
  onPostCpt,
  postDisabled = false,
}: EraProcedureSubTableProps) => {
  const showPostColumn = typeof onPostCpt === "function";
  if (!procedures?.length) {
    return (
      <Box
        sx={{
          py: 3,
          textAlign: "center",
          border: "1px dashed",
          borderColor: "grey.300",
          borderRadius: 1,
        }}
      >
        <Text variant="body2" color="text.secondary">
          No procedure lines for this claim.
        </Text>
      </Box>
    );
  }

  const rows = procedures.map(deriveCptAmounts);

  const totals: CptAmounts = rows.reduce<CptAmounts>(
    (acc, r) => ({
      bill: add(acc.bill, r.bill),
      insurancePaid: add(acc.insurancePaid, r.insurancePaid),
      allowed: add(acc.allowed, r.allowed),
      deduct: add(acc.deduct, r.deduct),
      coIns: add(acc.coIns, r.coIns),
      coPay: add(acc.coPay, r.coPay),
      patientResp: add(acc.patientResp, r.patientResp),
      contractAdj: add(acc.contractAdj, r.contractAdj),
      otherAdj: add(acc.otherAdj, r.otherAdj),
      balance: add(acc.balance, r.balance),
    }),
    {
      bill: null,
      insurancePaid: null,
      allowed: null,
      deduct: null,
      coIns: null,
      coPay: null,
      patientResp: null,
      contractAdj: null,
      otherAdj: null,
      balance: null,
    }
  );

  return (
    <TableContainer
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 1,
      }}
    >
      <Table
        size="small"
        sx={{
          "& td, & th": { borderColor: "grey.200" },
        }}
      >
        <TableHead>
          <TableRow sx={headerSx}>
            {ERA_PROCEDURE_COLUMNS.map((c) => (
              <TableCell key={c.key}>{c.label}</TableCell>
            ))}
            {/* ECRH-5475 — denial details captured at ERA posting time, in the
                ticket sequence: Status (Denied) -> Denial Code -> Comments. */}
            {/* <TableCell align="center">Denied</TableCell> */}
            <TableCell>Denial Code</TableCell>
            <TableCell>Comments</TableCell>
            {showPostColumn && <TableCell align="center">Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {procedures.map((pc, pcIndex) => {
            const {
              bill,
              insurancePaid,
              allowed,
              deduct,
              coIns,
              coPay,
              patientResp,
              contractAdj,
              otherAdj,
              // Balance column hidden from ERA procedure tables.
              // balance,
            } = rows[pcIndex];
            const isPosted = Boolean(pc.posted);
            // ECRH-5475 — grey the payment figures when the line is denied.
            const denied = Boolean(pc.denied);
            const amountSx = denied ? deniedAmountSx : undefined;

            return (
              <TableRow
                key={pc.uuid ?? `${extractCpt(pc)}-${pcIndex}`}
                sx={{
                  "& td": {
                    ...cellSx,
                    borderBottom: "1px solid",
                    borderColor: "grey.200",
                  },
                }}
              >
                <TableCell>{extractCpt(pc)}</TableCell>
                <TableCell sx={amountSx}>{fmt(bill)}</TableCell>
                <TableCell sx={amountSx}>{fmt(insurancePaid)}</TableCell>
                <TableCell sx={amountSx}>{fmt(allowed)}</TableCell>
                <TableCell sx={amountSx}>{fmt(deduct)}</TableCell>
                <TableCell sx={amountSx}>{fmt(coIns)}</TableCell>
                <TableCell sx={amountSx}>{fmt(coPay)}</TableCell>
                <TableCell sx={amountSx}>{fmt(patientResp)}</TableCell>
                <TableCell sx={amountSx}>{fmt(contractAdj)}</TableCell>
                <TableCell sx={amountSx}>{fmt(otherAdj)}</TableCell>
                {/* Balance column hidden from ERA procedure tables. */}
                {/* <TableCell sx={amountSx}>{fmt(balance)}</TableCell> */}
                {/* Status shows the line's posting status from the backend. The
                    Post / Posted button lives in the Action column below. */}
                <TableCell>
                  <StatusCell pc={pc} />
                </TableCell>
                {/* ECRH-5475 — denial details posted from the ERA screen, in the
                    ticket sequence: Status (above) -> Denial Code -> Comments. */}
                {/* <TableCell align="center">
                  <Checkbox size="small" checked={Boolean(pc.denied)} disabled />
                </TableCell> */}
                <TableCell>{pc.denialCode || "-"}</TableCell>
                <TableCell>{pc.comments || "-"}</TableCell>
                {showPostColumn && (
                  <TableCell align="center">
                    <StyledButtonV2
                      label={isPosted ? "Posted" : "Post"}
                      variant="outlined"
                      disabled={isPosted || postDisabled}
                      onClick={() => onPostCpt?.(pcIndex)}
                      sx={{
                        minWidth: 72,
                        height: 26,
                        borderRadius: "16px",
                        ...DEFAULT_FONTSIZE,
                        ...(isPosted && {
                          bgcolor: "transparent",
                          color: "grey.600",
                          borderColor: "grey.400",
                          "&.Mui-disabled": {
                            bgcolor: "transparent",
                            color: "grey.600",
                            borderColor: "grey.400",
                          },
                        }),
                      }}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })}

          <TableRow
            sx={{
              "& td": {
                ...cellSx,
                fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
                bgcolor: "grey.50",
                borderTop: "1px solid",
                borderColor: "grey.300",
              },
            }}
          >
            <TableCell>Total</TableCell>
            <TableCell>{fmt(totals.bill)}</TableCell>
            <TableCell>{fmt(totals.insurancePaid)}</TableCell>
            <TableCell>{fmt(totals.allowed)}</TableCell>
            <TableCell>{fmt(totals.deduct)}</TableCell>
            <TableCell>{fmt(totals.coIns)}</TableCell>
            <TableCell>{fmt(totals.coPay)}</TableCell>
            <TableCell>{fmt(totals.patientResp)}</TableCell>
            <TableCell>{fmt(totals.contractAdj)}</TableCell>
            <TableCell>{fmt(totals.otherAdj)}</TableCell>
            {/* Balance column hidden from ERA procedure tables. */}
            {/* <TableCell>{fmt(totals.balance)}</TableCell> */}
            <TableCell />
            {/* ECRH-5475 — Denial Code / Comments columns (Denied commented). */}
            {/* <TableCell /> */}
            <TableCell />
            <TableCell />
            {showPostColumn && <TableCell />}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EraProcedureSubTable;
